package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.cmc.error
import com.siriusxm.cmc.success
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rememberCapability
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.AggregatorStyles
import com.siriusxm.pia.views.unifiedaggregator.ProducerBaseTypes
import com.siriusxm.pia.views.unifiedaggregator.ProducerDetail
import com.siriusxm.pia.views.unifiedaggregator.ProducerEdit
import com.siriusxm.pia.views.unifiedaggregator.ProducerPartials
import com.siriusxm.pia.views.unifiedaggregator.ProducerRelationshipPermissions
import com.siriusxm.pia.views.unifiedaggregator.ProducerTab
import com.siriusxm.pia.views.unifiedaggregator.producerDetailBasePath
import com.siriusxm.pia.views.unifiedaggregator.navigate
import com.siriusxm.pia.views.unifiedaggregator.events.*
import unifiedcontent.aggregator.AuditResourceType
import unifiedcontent.aggregator.PermissionModel
import unifiedcontent.aggregator.ProducerDetails
import unifiedcontent.aggregator.ProducerEventType
import unifiedcontent.aggregator.TypePermission
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text


/**
 * A view of the configuration of a producer.
 * Users authorized for ProducerUpdate can edit, disable, or enable the producer.
 * A producer cannot be disabled if it has any allowed types or partials configured.
 *
 * @param tab which detail tab to show. Driven entirely by the URL (UCA-1028) rather than local
 *   component state, so a link to a specific tab can be shared and survives a reload. Resolved by
 *   [com.siriusxm.pia.views.unifiedaggregator.parseProducerPath], which maps an unrecognised tab
 *   segment to [ProducerTab.Default] — `tabView` only falls back to the first tab when its selection
 *   is null, so an unknown id reaching it would render a tab bar with nothing selected.
 * @param filters the Events and Errors tab filter selections, decoded from the URL query. Like [tab]
 *   these are URL state rather than component state, so a filtered view is a link. This page is the
 *   only place they are written: it navigates to itself with a new query, which is why both event
 *   views below are fully controlled and hold no filter state of their own.
 */
@Composable
fun Producer(
    aggregator: AggregatorService,
    producer: ProducerDetails,
    tab: ProducerTab = ProducerTab.Default,
    filters: EventFilters = EventFilters()
) {
    var showDisableDialog by remember { mutableStateOf(false) }
    var showEnableDialog by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val canUpdateProducer by aggregator.context.rememberCapability(
        producerUpdateCapability(producer.id)
    )

    // Fetch full producer details to get computed fields like hasPublishPermission.
    // Starts null so we don't flash stale permission status from a previously selected producer.
    var fullProducer by remember { mutableStateOf<ProducerDetails?>(null) }
    LaunchedEffect(producer.id) {
        fullProducer = null
        fullProducer = aggregator.clientApi.getProducer(producer.id)
    }

    // Check if producer can be disabled (no types, partials, or type permissions configured)
    val canDisable = producer.allowedTypes.isNullOrEmpty()
            && producer.allowedPartials.isNullOrEmpty()
            && producer.typePermissions.isNullOrEmpty()

    entityView({
        title = producer.name
        subTitle = producer.id

        if (canUpdateProducer) {
            action("Edit", true) {
                aggregator.navigate(ProducerEdit(producer.id))
            }

            // Disable/Enable action
            if (producer.disabled == true) {
                action {
                    title = "Enable"
                    primary = false
                    action {
                        showEnableDialog = true
                    }
                }
            } else {
                action {
                    title = "Disable"
                    primary = false
                    action {
                        if (canDisable) {
                            showDisableDialog = true
                        } else {
                            aggregator.notifications.error("This producer cannot be disabled because it has allowed types or partials configured. Remove all allowed types and partials before disabling.")
                        }
                    }
                }
            }
        }
    }) {
        // Show disabled status badge if disabled
        if (producer.disabled == true) {
            Div({
                style {
                    marginBottom(16.px)
                }
            }) {
                Badge("DISABLED", BadgeColor.RED)
            }
        }

        // Disable confirmation dialog
        if (showDisableDialog) {
            DisableConfirmationDialog(
                entityType = "producer",
                entityName = producer.name,
                entityId = producer.id
            ) { confirmed ->
                if (confirmed) {
                    scope.launch {
                        try {
                            aggregator.updateProducer(producer.copy(disabled = true))
                            aggregator.notifications.success("Producer disabled successfully")
                        } catch (e: Exception) {
                            aggregator.notifications.error("Failed to disable producer: ${e.message}")
                        }
                    }
                }
                showDisableDialog = false
            }
        }

        // Enable confirmation dialog
        if (showEnableDialog) {
            EnableConfirmationDialog(
                entityType = "producer",
                entityName = producer.name
            ) { confirmed ->
                if (confirmed) {
                    scope.launch {
                        try {
                            aggregator.updateProducer(producer.copy(disabled = false))
                            aggregator.notifications.success("Producer enabled successfully")
                        } catch (e: Exception) {
                            aggregator.notifications.error("Failed to enable producer: ${e.message}")
                        }
                    }
                }
                showEnableDialog = false
            }
        }
        // LEGACY is the default model, so it is not shown: labelling a producer "legacy" would read as
        // deprecated. Only a UCP producer gets a Permission Model row.
        val isUcp = producer.permissionModel.effective() == PermissionModel.UCP
        val hasDetails = isUcp
                || producer.clientId != null
                || producer.serviceRegistryId != null
                || producer.awsAccountId != null
                || producer.contact?.isNotEmpty() == true
                || producer.feedbackTargetArn != null
                || producer.slackChannel != null

        Column(gap = 20.px) {
        if (hasDetails) {
            box {
                detailGrid {
                    if (isUcp) {
                        detail("Permission Model") {
                            PermissionModelBadge(producer.permissionModel)
                        }
                    }

                    producer.clientId?.let {
                        detail("OAuth ClientId", it)
                    }

                    producer.serviceRegistryId?.let { serviceId ->
                        detail("Service Registry") {
                            A(href = "#service-registry/services/${encodeURIComponent(serviceId)}") {
                                Text("View in Registry")
                            }
                        }
                    }

                    if (producer.awsAccountId != null) {
                        detail("AWS Account") {
                            Text(producer.awsAccountId!!)
                            if (fullProducer?.hasPublishPermission == false) {
                                Span({
                                    style { marginLeft(8.px) }
                                }) {
                                    Badge("Not Authorized", BadgeColor.RED)
                                }
                            }
                        }
                    }
                    if (producer.contact?.isNotEmpty() == true) {
                        detail("Contact") {
                            producer.contact?.forEach {
                                Div {
                                    Text(it.email)
                                }
                            }
                        }
                    }
                    producer.feedbackTargetArn?.let {
                        detail("Feedback Target ARN", producer.feedbackTargetArn)
                    }

                    producer.slackChannel?.let {
                        detail("Slack") {
                            A(
                                href = "https://siriusxm-pandora.slack.com/channels/${it}",
                                { target(ATarget.Blank) }) {
                                Img(src = "images/slack.png") {
                                    classes(AggregatorStyles.slackIcon)
                                }
                                Text(it)
                            }
                        }
                    }
                }
            }
        }

        key(producer.id) {
        // tabView renders the tab bar and the tab content as two sibling elements with no
        // wrapper of its own. Wrap them in a single Div so the enclosing Column(gap) sees the
        // whole tab control as one flex item; otherwise the gap is inserted between the tab bar
        // and its content. Spacing inside the tab control stays owned by tabView.
        Div {
        tabView {
            // URL-driven tab selection. tabNavigation appends "/{tabId}" to the base path and makes
            // tabView fully controlled, so the URL is the only source of truth for which tab is
            // showing. The base path is built from the same RouteLocation.toPath() every producer URL
            // goes through, so it encodes the id identically. Switching tabs this way drops the query,
            // which is intended — the Events and Errors tabs have different filter vocabularies.
            tabNavigation(aggregator, producerDetailBasePath(producer.id), tab.id)

            // Tab ids come from ProducerTab rather than being derived from the title, so the URL
            // segment is pinned to the enum and renaming a title can't silently break shared links.
            tab("Permissions", ProducerTab.Permissions.id) {
                val permissions = producer.typePermissions

                Column(gap = 20.px) {
                // Base Types section — shows which entity types the producer can
                // publish base/full updates for. Users authorized for ProducerUpdate see an "Edit" button
                // that navigates to the dedicated BaseTypesEditor page.
                box({
                    title = "Base Types"
                    paddedContent = false
                    header({
                        instruction = "Entity types this producer can publish."
                    })
                    if (canUpdateProducer) {
                        action {
                            title = "Edit"
                            primary = false
                            this.action {
                                aggregator.navigate(ProducerBaseTypes(producer.id))
                            }
                        }
                    }
                }) {
                    val baseTypes = permissions?.filter { it.base == true }.orEmpty()
                    if (baseTypes.isEmpty()) {
                        boxMessage("This producer cannot publish entity updates.")
                    } else {
                        table<TypePermission> {
                            items(baseTypes)
                            column {
                                content { perm ->
                                    A(href = "#aggregator/types/entities/${encodeURIComponent(perm.type)}") {
                                        Text(aggregator.entityType(perm.type)?.name ?: perm.type)
                                    }
                                }
                            }
                        }
                    }
                }

                // Partials section — shows which partials the producer can submit
                // and for which entity types. Users authorized for ProducerUpdate see an "Edit" button
                // that navigates to the dedicated PartialsEditor page.
                box({
                    title = "Partials"
                    paddedContent = false
                    header({
                        instruction = "Partial updates this producer can submit."
                    })
                    if (canUpdateProducer) {
                        action {
                            title = "Edit"
                            primary = false
                            this.action {
                                aggregator.navigate(ProducerPartials(producer.id))
                            }
                        }
                    }
                }) {
                    // Unrestricted partials (UCA-1162) show once as "All types" rather than as the
                    // schema types the server expands them to in typePermissions.
                    val partialAssignments = partialAssignments(
                        permissions.orEmpty(),
                        producer.unrestrictedPartials.orEmpty().toSet()
                    )

                    if (partialAssignments.isEmpty()) {
                        boxMessage("This producer cannot submit partial updates.")
                    } else {
                        table<PartialAssignment> {
                            items(partialAssignments)

                            column("Partial") {
                                content { assignment ->
                                    val partialId = assignment.partialId
                                    A(href = "#aggregator/types/partials/${encodeURIComponent(partialId)}") {
                                        Text(aggregator.partial(partialId)?.name ?: partialId)
                                    }
                                }
                            }

                            column("Types") {
                                content { assignment ->
                                    Div {
                                        if (assignment.allTypes) {
                                            Text(ALL_TYPES_LABEL)
                                        }
                                        assignment.types.forEachIndexed { index, typeId ->
                                            if (index > 0) Text(", ")
                                            A(href = "#aggregator/types/entities/${encodeURIComponent(typeId)}") {
                                                Text(aggregator.entityType(typeId)?.name ?: typeId)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // Relationships section — shows which editable relationship types
                // the producer can create and delete. Users authorized for ProducerUpdate see an "Edit" button
                // that navigates to the dedicated RelationshipPermissionsEditor page.
                box({
                    title = "Relationships"
                    paddedContent = false
                    header({
                        instruction = "Editable relationship types this producer can create and delete."
                    })
                    if (canUpdateProducer) {
                        action {
                            title = "Edit"
                            primary = false
                            this.action {
                                aggregator.navigate(ProducerRelationshipPermissions(producer.id))
                            }
                        }
                    }
                }) {
                    val allowedRelationships = producer.allowedRelationships.orEmpty()
                    if (allowedRelationships.isEmpty()) {
                        boxMessage("This producer cannot create or delete relationships.")
                    } else {
                        table<String> {
                            items(allowedRelationships.sorted())

                            column {
                                content { relationshipId ->
                                    val schema = aggregator.relationships.find { it.id == relationshipId }
                                    if (schema != null) {
                                        Text(schema.name)
                                    } else {
                                        Text(relationshipId)
                                    }
                                }
                            }
                        }
                    }
                }

                // The UCP team that owns this producer; membership is administered in UCP (UCA-1212).
                val teamId = fullProducer?.teamId ?: producer.teamId
                TeamBox(aggregator, teamId)

                // UCP producers are authorized through UCP, not through user agents, so the box is
                // irrelevant. The same goes for a producer linked to a team: its members live in UCP.
                if (!isUcp && teamId.isNullOrBlank()) {
                    ServiceUsersBox(aggregator, producer.id, ServiceUsersType.PRODUCER)
                }
                }
            }

            tab("Events", ProducerTab.Events.id) {
                val allowedEntityTypes = producer.typePermissions
                    ?.map { it.type }
                    .orEmpty()
                ProducerValidEvents(
                    aggregator,
                    allowedEntityTypes,
                    filters = filters,
                    // Writing a filter is a navigation: same destination, new query. Push rather than
                    // replace, so browser Back steps back through filter states — which is what makes
                    // the feature usable for comparing two views. The key supplies the path and
                    // EventFilters supplies the query; neither knows about the other.
                    onFiltersChange = { updated ->
                        aggregator.navigate(
                            ProducerDetail(producer.id, ProducerTab.Events),
                            updated.toQuery()
                        )
                    }
                ) { range, queryId ->
                    if (queryId != null) {
                        aggregator.clientApi.getProducerEvents(
                            producer.id,
                            queryId = queryId
                        )
                    } else if (range != null) {
                        val timeRange = range.range()
                        aggregator.clientApi.getProducerEvents(
                            producer.id,
                            timeRange.start, timeRange.endInclusive,
                            eventType = ProducerEventType.ENTITIES
                        )
                    } else {
                        throw IllegalArgumentException()
                    }
                }
            }

            tab("Errors", ProducerTab.Errors.id) {
                producerEvents(aggregator, producer, filters)
            }

            tab("History", ProducerTab.History.id) {
                AuditHistory(aggregator, AuditResourceType.PRODUCER, producer.id)
            }
        }
        }
        }
        }
    }
}


/**
 * The producer events view, grouped by error reason.
 *
 * @param filters the time range and error-message search, held in the URL rather than in the view, so
 *   that a filtered error list can be shared. Compare [ProducersDashboard], which renders the same
 *   view with local state and writes nothing to the URL.
 */
@Composable
fun producerEvents(
    aggregator: AggregatorService,
    producer: ProducerDetails,
    filters: EventFilters = EventFilters()
) {
    GroupedInvalidEvents(
        aggregator,
        filters = filters,
        onFiltersChange = { updated ->
            aggregator.navigate(
                ProducerDetail(producer.id, ProducerTab.Errors),
                updated.toQuery()
            )
        }
    ) { range, queryId ->
        if (queryId != null) {
            aggregator.clientApi.getProducerEvents(producer.id, queryId = queryId)
        } else if (range != null) {
            val range = range.range()
            aggregator.clientApi.getProducerEvents(
                producer.id,
                range.start, range.endInclusive
            )
        } else {
            throw IllegalArgumentException()
        }
    }
}
